import { Col, Row } from "antd";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdAdminPanelSettings,
  MdOutlineFamilyRestroom,
  MdWorkOutline,
} from "react-icons/md";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_EXERCISE_FRAME,
  FAMILY_SITUATION_LMF,
  SITUATION_PROFESSIONAL,
  SITUATION_PROFESSIONAL_INDEPENDENT,
  SITUATION_PROFESSIONAL_RETIRED,
  SITUATION_PROFESSIONAL_SALARIED_PRIVATE_SECTOR,
  SITUATION_PROFESSIONAL_SALARIED_PUBLIC_SECTOR,
  SITUATION_PROFESSIONAL_STUDENT,
  SOCIO_PROFESSIONAL_CATEGORIES,
  SUBSCRIPTION_FORM_KEYS,
} from "utils/constants";

export const showExerciseFrame = (value) =>
  [
    SOCIO_PROFESSIONAL_CATEGORIES.FARMERS,
    SOCIO_PROFESSIONAL_CATEGORIES.ARTISANS,
    SOCIO_PROFESSIONAL_CATEGORIES.LIBERAL_PROFESSIONS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.TRADERS_AND_SIMILAR,
    SOCIO_PROFESSIONAL_CATEGORIES.ENTREPRENEURS,
  ].includes(value);

const getSubcategoriesForCategory = (category) => {
  switch (category) {
    case SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR:
      return SITUATION_PROFESSIONAL_SALARIED_PRIVATE_SECTOR;
    case SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR:
      return SITUATION_PROFESSIONAL_SALARIED_PUBLIC_SECTOR;
    case SITUATION_PROFESSIONAL.INDEPENDENT:
      return SITUATION_PROFESSIONAL_INDEPENDENT;
    case SITUATION_PROFESSIONAL.STUDENT:
      return SITUATION_PROFESSIONAL_STUDENT;
    case SITUATION_PROFESSIONAL.RETIRED:
      return SITUATION_PROFESSIONAL_RETIRED;
    default:
      return {};
  }
};

const LMF_SITUATION_PROFESSIONAL = {
  [SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB]:
    SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
  [SITUATION_PROFESSIONAL.STUDENT]: SITUATION_PROFESSIONAL.STUDENT,
  [SITUATION_PROFESSIONAL.INDEPENDENT]: SITUATION_PROFESSIONAL.INDEPENDENT,
  [SITUATION_PROFESSIONAL.RETIRED]: SITUATION_PROFESSIONAL.RETIRED,
  [SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR]:
    SITUATION_PROFESSIONAL.SALARIED_PRIVATE_SECTOR,
  [SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR]:
    SITUATION_PROFESSIONAL.SALARIED_PUBLIC_SECTOR,
};

const Activity = ({
  setPercent,
  setDisabled,
  isFieldRequired,
  initialValues,
  bindField,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const socioProfessionalCategoryPath =
    "project.contact.socioProfessionalCategory";
  const socioProfessionalCategoryField = bindField(
    socioProfessionalCategoryPath,
    {
      label: SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.LABEL,
      required: isFieldRequired(socioProfessionalCategoryPath),
      onChange: (value) => {
        set(
          initialValues,
          "project.contact.fields.socioProfessionalSubCategory",
          null,
        );
        if (
          (
            get(
              initialValues,
              "insurancePlan.config.additionalDocuments",
              [],
            ) || []
          ).includes("proof.pensionFund")
        ) {
          setDisabled((prev) => ({
            ...prev,
            [SUBSCRIPTION_FORM_KEYS.PROOF.PENSION_FUND]:
              value === SOCIO_PROFESSIONAL_CATEGORIES.RETIREES,
          }));
        }
        if (!showExerciseFrame(value)) {
          set(
            initialValues,
            "project.contact.exerciseFrame",
            CONTACT_EXERCISE_FRAME.EMPLOYEE,
          );
        }
        setDisabled((prev) => ({
          ...prev,
          project: {
            ...prev.project,
            contact: {
              ...prev.project.contact,
              exerciseFrame: !showExerciseFrame(value),
              fields: {
                ...get(prev, "project.contact.fields", {}),
                socioProfessionalSubCategory:
                  !value ||
                  value === SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
              },
            },
          },
        }));
        setPercent(initialValues);
      },
    },
  );

  const socioProfessionalSubCategoryPath =
    "project.contact.fields.socioProfessionalSubCategory";
  const socioProfessionalSubCategoryField = bindField(
    socioProfessionalSubCategoryPath,
    {
      label:
        SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.SUB_OPTIONS.CSP_LABEL,
      required: isFieldRequired(socioProfessionalSubCategoryPath),
      onChange: () => setPercent(initialValues),
    },
  );

  const familySituationPath = "project.contact.fields.familySituation";
  const familySituationField = bindField(familySituationPath, {
    label: SUBSCRIPTION.FORM.LMF_FAMILY_SITUATION.LABEL,
    required: isFieldRequired(familySituationPath),
    onChange: () => setPercent(initialValues),
  });

  const selectedCategory = get(initialValues, socioProfessionalCategoryPath);
  const subcategories = getSubcategoriesForCategory(selectedCategory);
  const currentSubCategory = get(
    initialValues,
    socioProfessionalSubCategoryPath,
  );
  const validSubCategoryDefault =
    selectedCategory &&
    currentSubCategory &&
    Object.values(subcategories).includes(currentSubCategory)
      ? currentSubCategory
      : null;

  return (
    <div
      className="subscription-form--wrapper"
      id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.ACTIVITY}-section`}
    >
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.ACTIVITY.TITLE}
      >
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <Input
              label={SUBSCRIPTION.FORM.PROFILE.LABEL}
              icon={<MdAdminPanelSettings size={16} />}
              defaultValue={
                SUBSCRIPTION.FORM.PROFILE.OPTIONS[
                  get(initialValues, "project.contact.fields.profile")
                ]
              }
              disabled={true}
            />
          </Col>
          <Col lg={24} xs={24}>
            <FormField
              path={socioProfessionalCategoryField.path}
              label={socioProfessionalCategoryField.label}
              required={socioProfessionalCategoryField.required}
              disabled={socioProfessionalCategoryField.disabled}
            >
              {(field) => (
                <Select
                  options={Object.entries(LMF_SITUATION_PROFESSIONAL).map(
                    ([key, value]) => ({
                      value,
                      label:
                        SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.OPTIONS[key],
                    }),
                  )}
                  label={socioProfessionalCategoryField.label}
                  icon={<MdWorkOutline size={16} />}
                  placeholder={
                    SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.PLACEHOLDER
                  }
                  defaultValue={socioProfessionalCategoryField.defaultValue}
                  {...field}
                  onChange={socioProfessionalCategoryField.onChange}
                  onBlur={socioProfessionalCategoryField.onBlur}
                />
              )}
            </FormField>
          </Col>
          {selectedCategory &&
            selectedCategory !== SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB &&
            Object.keys(subcategories).length > 0 && (
              <Col lg={24} xs={24}>
                <FormField
                  path={socioProfessionalSubCategoryField.path}
                  label={socioProfessionalSubCategoryField.label}
                  required={socioProfessionalSubCategoryField.required}
                  disabled={socioProfessionalSubCategoryField.disabled}
                >
                  {(field) => (
                    <Select
                      key={selectedCategory}
                      options={Object.entries(subcategories).map(
                        ([key, value]) => ({
                          value,
                          label:
                            SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL
                              .SUB_OPTIONS[selectedCategory]?.OPTIONS?.[key] ||
                            key,
                        }),
                      )}
                      label={socioProfessionalSubCategoryField.label}
                      icon={<MdWorkOutline size={16} />}
                      placeholder={
                        SUBSCRIPTION.FORM.SITUATION_PROFESSIONAL.SUB_OPTIONS
                          .PLACEHOLDER
                      }
                      defaultValue={validSubCategoryDefault}
                      {...field}
                      onChange={socioProfessionalSubCategoryField.onChange}
                      onBlur={socioProfessionalSubCategoryField.onBlur}
                    />
                  )}
                </FormField>
              </Col>
            )}
          <Col lg={24} xs={24}>
            <FormField
              path={familySituationField.path}
              label={familySituationField.label}
              required={familySituationField.required}
              disabled={familySituationField.disabled}
            >
              {(field) => (
                <Select
                  label={familySituationField.label}
                  icon={<MdOutlineFamilyRestroom size={16} />}
                  placeholder={
                    SUBSCRIPTION.FORM.LMF_FAMILY_SITUATION.PLACEHOLDER
                  }
                  type="simple"
                  options={Object.entries(FAMILY_SITUATION_LMF).map(
                    ([key, value]) => ({
                      value,
                      label: SUBSCRIPTION.FORM.LMF_FAMILY_SITUATION.OPTIONS[key],
                    }),
                  )}
                  defaultValue={familySituationField.defaultValue}
                  {...field}
                  onChange={familySituationField.onChange}
                  onBlur={familySituationField.onBlur}
                />
              )}
            </FormField>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default Activity;
